import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { environment } from "../../../environments/environment";
import { filter, interval, first } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class VersionCheckerService {
  private checkInterval = 30 * 1000; // Check every 30 seconds

  constructor(
    private router: Router,
    private swUpdate: SwUpdate
  ) { }

  async checkForNewVersion() {
    try {
      const latestVersion = environment.version;
      let currentVersion = localStorage.getItem('app_version');

      if (!currentVersion) {
        localStorage.setItem('app_version', latestVersion);
        currentVersion = latestVersion;
      }

      if (currentVersion !== latestVersion) {
        localStorage.setItem('app_version', latestVersion);
        // Force reload to get new version
        this.forceReload();
      }
    } catch (error) {
      console.error('Error checking version:', error);
    }
  }

  private async forceReload(): Promise<void> {
    // Clear all caches before reload
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map(name => caches.delete(name)));
    }
    window.location.reload();
  }

  private async activateAndReload() {
    if (this.swUpdate.isEnabled) {
      try {
        await this.swUpdate.activateUpdate();
        this.forceReload();
      } catch (err) {
        console.error('Error activating update:', err);
        this.forceReload();
      }
    } else {
      this.forceReload();
    }
  }

  startVersionCheck() {
    if (this.swUpdate.isEnabled) {
      // Check for updates IMMEDIATELY on startup
      this.swUpdate.checkForUpdate().then(hasUpdate => {
        if (hasUpdate) {
          console.log('Update available on startup - activating...');
          this.activateAndReload();
        }
      }).catch(err => console.error('Error checking for update on startup', err));

      // Listen for available updates - AUTO RELOAD when ready
      this.swUpdate.versionUpdates
        .pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'))
        .subscribe(event => {
          console.log('New version ready - auto reloading...');
          this.activateAndReload();
        });

      // Keep checking for updates periodically
      interval(this.checkInterval).subscribe(() => {
        this.swUpdate.checkForUpdate().then(hasUpdate => {
          if (hasUpdate) {
            console.log('New version detected - activating...');
            this.activateAndReload();
          }
        }).catch(err => console.error('Error checking for update', err));
      });

    } else {
      // Fallback for non-SW environments (dev)
      this.checkForNewVersion();
      setInterval(() => {
        this.checkForNewVersion();
      }, this.checkInterval);
    }
  }
}
